import { A } from '@solidjs/router';
import { Ban, ChevronDown, ChevronLeft, ChevronRight, CircleDot, Eye, GitCommitHorizontal, GitMerge, GitPullRequest, GitPullRequestClosed, SquareChartGantt } from 'lucide-solid';
import { For, Show, createEffect, createMemo, createSignal, onCleanup, type Component } from 'solid-js';

import { ErrorState, SkeletonBlock } from '../components/common';
import { getRepoDefaultBranch, getRepoByUri, getRepoLog, type CommitEntry, type GitHashValue, type RepoContext } from '../lib/api/repos';
import { getAppviewOpenIssueCount, listRecentOpenIssues, type IssueSummary } from '../lib/api/issues';
import { getAppviewOpenPullCount, listRecentOpenPulls, type PullSummary } from '../lib/api/pulls';
import { listWatchRecords, type WatchRecord } from '../lib/api/watches';
import { useAuth } from '../lib/auth';
import { createQuery, useQueryClient } from '../lib/atproto-query';
import { useLiveEvents } from '../lib/live-events';
import { formatRelativeTime, getErrorMessage } from '../lib/repo-utils';

type WatchedRepo = {
	watch: WatchRecord;
	repo: RepoContext;
	issues: IssueSummary[];
	pulls: PullSummary[];
	openIssueCount: number | null;
	openPullCount: number | null;
	commits: CommitEntry[];
};

const hashText = (hash?: GitHashValue): string => {
	if (typeof hash === 'string') return hash;
	if (Array.isArray(hash)) return hash.map((part) => part.toString(16).padStart(2, '0')).join('');
	return hash?.String ?? hash?.string ?? '';
};

const commitDate = (commit: CommitEntry): string | undefined => commit.author?.When ?? commit.committer?.When;

const commitMessage = (message: string): string => message.split('\n')[0]?.trim() || 'untitled commit';

const WATCHED_WORK_LIMIT = 3;

const loadWatchedRepo = async (watch: WatchRecord): Promise<WatchedRepo | null> => {
	try {
		const repo = await getRepoByUri(watch.value.repo);
		const branch = await getRepoDefaultBranch(repo).catch(() => undefined);
		const ref = branch?.name ?? branch?.branch ?? branch?.reference?.name ?? 'HEAD';
		// Only the newest few open items are shown, so page the APIs instead of
		// listing every issue and pull request in the repository.
		const [issues, pulls, log] = await Promise.all([
			listRecentOpenIssues(repo, WATCHED_WORK_LIMIT).catch(() => []),
			listRecentOpenPulls(repo, WATCHED_WORK_LIMIT).catch(() => []),
			getRepoLog(repo, ref, { limit: 30 }).catch(() => ({ commits: [] } as { commits: CommitEntry[] })),
		]);

		return {
			watch,
			repo,
			issues,
			pulls,
			openIssueCount: null,
			openPullCount: null,
			commits: log.commits.slice(0, 30),
		};
	} catch {
		return null;
	}
};

const WorkLink: Component<{ href: string; kind: 'issue' | 'pull'; item: IssueSummary | PullSummary }> = (props) => {
	const isPull = () => props.kind === 'pull';
	return (
		<A href={props.href} class="untangled-watch-work-link" aria-label={`${props.item.state} ${props.kind}: ${props.item.value.title} #${props.item.number}`}>
			<Show when={isPull()} fallback={props.item.state === 'open' ? <CircleDot class="size-4 text-green-600" /> : <Ban class="size-4 text-gray-500" />}>
				{props.item.state === 'open' ? <GitPullRequest class="size-4 text-purple-500" /> : props.item.state === 'merged' ? <GitMerge class="size-4 text-purple-500" /> : <GitPullRequestClosed class="size-4 text-gray-500" />}
			</Show>
			<span class="min-w-0 flex-1 truncate">{props.item.value.title}</span>
			<span class="shrink-0 text-xs text-gray-500 dark:text-gray-400">#{props.item.number}</span>
		</A>
	);
};

const CommitDetails: Component<{ repo: RepoContext; commits: CommitEntry[] }> = (props) => (
	<details class="untangled-watch-commit-details">
		<summary class="untangled-watch-stat untangled-watch-stat-button" title="show recent commits">
			<GitCommitHorizontal class="size-4" />
			<span>recent commits</span>
		</summary>
		<div class="untangled-watch-commit-popover">
			<div class="untangled-watch-popover-heading">recent commits</div>
			<Show when={props.commits.length > 0} fallback={<p class="untangled-watch-muted">no commit data available</p>}>
				<For each={props.commits.slice(0, 8)}>
					{(commit) => {
						const hash = hashText(commit.hash ?? commit.this);
						return (
							<A href={`/${props.repo.owner.handle}/${props.repo.slug}/commit/${encodeURIComponent(hash)}`} class="untangled-watch-commit-row">
								<span class="untangled-watch-commit-hash">{hash.slice(0, 7)}</span>
								<span class="min-w-0 flex-1 truncate">{commitMessage(commit.message)}</span>
								<span class="shrink-0 text-xs text-gray-500 dark:text-gray-400">{commitDate(commit) ? formatRelativeTime(commitDate(commit)!) : ''}</span>
							</A>
						);
					}}
				</For>
			</Show>
		</div>
	</details>
);

const WatchedRepoRow: Component<{ item: WatchedRepo; expanded: boolean; onToggle: () => void }> = (props) => {
	const repo = () => props.item.repo;
	const repoPath = () => `/${repo().owner.handle}/${repo().slug}`;
	const openIssues = () => props.item.issues.filter((issue) => issue.state === 'open');
	const openPulls = () => props.item.pulls.filter((pull) => pull.state === 'open');
	const latestCommit = () => props.item.commits[0];

	return (
		<article class="untangled-watched-repo" role="listitem">
			<div class="untangled-watched-repo-summary">
				<button
					type="button"
					class="untangled-watched-repo-identity"
					aria-expanded={props.expanded}
					onClick={props.onToggle}
				>
					<span class="untangled-watched-repo-title">
						<Eye class="size-4 shrink-0 text-blue-500" />
						<span>{repo().owner.handle}/{repo().slug}</span>
					</span>
					<Show when={repo().record.value.description}>
						<span class="untangled-watched-repo-description">{repo().record.value.description}</span>
					</Show>
				</button>

				<span class="untangled-watched-work-summary" aria-label="open work">
					<Show
						when={props.item.openIssueCount !== null && props.item.openPullCount !== null}
						fallback={<span class="untangled-watched-work-none">&hellip;</span>}
					>
						<Show
							when={props.item.openIssueCount! > 0 || props.item.openPullCount! > 0}
							fallback={<span class="untangled-watched-work-none">&mdash;</span>}
						>
							<Show when={props.item.openIssueCount! > 0}>
								<span><CircleDot class="size-4 text-green-600" />{props.item.openIssueCount} issues</span>
							</Show>
							<Show when={props.item.openPullCount! > 0}>
								<span><GitPullRequest class="size-4 text-purple-500" />{props.item.openPullCount} prs</span>
							</Show>
						</Show>
					</Show>
				</span>

				<span class="untangled-watched-activity-summary">
					<CommitDetails repo={repo()} commits={props.item.commits} />
					<Show when={latestCommit()}>
						<span class="untangled-watched-latest-commit">{commitMessage(latestCommit()!.message)}</span>
						<Show when={commitDate(latestCommit()!)}>
							<span class="untangled-watched-latest-time">{formatRelativeTime(commitDate(latestCommit()!)!)}</span>
						</Show>
					</Show>
				</span>

				<button
					type="button"
					class="untangled-watched-expand"
					aria-label={props.expanded ? 'collapse repository details' : 'expand repository details'}
					onClick={props.onToggle}
				>
					<ChevronDown class="untangled-watched-expand-icon size-4" />
				</button>
			</div>

			<Show when={props.expanded}>
				<div class="untangled-watched-repo-detail">
					<header class="untangled-watched-detail-header">
						<span>recent open work</span>
						<nav class="untangled-watched-quick-links" aria-label={`${repo().slug} quick links`}>
							<A href={repoPath()} title="open repository"><SquareChartGantt class="size-4" /></A>
							<A href={`${repoPath()}/issues`} title="open issues"><CircleDot class="size-4" /></A>
							<A href={`${repoPath()}/pulls`} title="open pull requests"><GitPullRequest class="size-4" /></A>
							<A href={`${repoPath()}/commits`} title="open commits"><GitCommitHorizontal class="size-4" /></A>
						</nav>
					</header>
					<div class="untangled-watched-work-grid">
						<section>
							<h2>issues</h2>
							<Show when={openIssues().length > 0} fallback={<p class="untangled-watch-muted">&mdash;</p>}>
								<div class="untangled-watch-work-list">
									<For each={openIssues().slice(0, 3)}>{(issue) => <WorkLink kind="issue" item={issue} href={`${repoPath()}/issues/${issue.number}`} />}</For>
								</div>
							</Show>
						</section>
						<section>
							<h2>pull requests</h2>
							<Show when={openPulls().length > 0} fallback={<p class="untangled-watch-muted">&mdash;</p>}>
								<div class="untangled-watch-work-list">
									<For each={openPulls().slice(0, 3)}>{(pull) => <WorkLink kind="pull" item={pull} href={`${repoPath()}/pulls/${pull.number}`} />}</For>
								</div>
							</Show>
						</section>
					</div>
				</div>
			</Show>
		</article>
	);
};

const WATCHED_PAGE_SIZE = 3;
const WATCHED_REPOS_QUERY_KEY = 'watched-repos-v2';
const WATCHED_SKELETON_ROWS = [0, 1, 2];

const WatchedReposSkeleton: Component = () => (
	<div class="untangled-watched-list untangled-watched-loading-list" role="status" aria-label="loading watched repositories">
		<div class="untangled-watched-list-heading" aria-hidden="true">
			<span>repository</span>
			<span>open work</span>
			<span>latest</span>
			<span></span>
		</div>
		<For each={WATCHED_SKELETON_ROWS}>
			{() => (
				<div class="untangled-watched-skeleton-row" aria-hidden="true">
					<div class="untangled-watched-skeleton-repo">
						<SkeletonBlock class="untangled-skeleton-on-dark h-4 w-3/5" />
						<SkeletonBlock class="untangled-skeleton-on-dark h-3 w-2/5" />
					</div>
					<SkeletonBlock class="untangled-skeleton-on-dark h-3 w-24" />
					<div class="untangled-watched-skeleton-latest">
						<SkeletonBlock class="untangled-skeleton-on-dark h-3 w-48 max-w-full" />
						<SkeletonBlock class="untangled-skeleton-on-dark h-3 w-16" />
					</div>
					<SkeletonBlock class="untangled-skeleton-on-dark size-4" />
				</div>
			)}
		</For>
	</div>
);

export const WatchedReposPanel: Component = () => {
	const auth = useAuth();
	const live = useLiveEvents();
	const queryClient = useQueryClient();
	const watchesQuery = createQuery(() => ({
		queryKey: [WATCHED_REPOS_QUERY_KEY, auth.currentDid()],
		enabled: Boolean(auth.currentDid()),
		staleTime: 86_400_000,
		queryFn: async () => {
			const watches = await listWatchRecords(auth.currentDid()!);
			const items = await Promise.all(watches.map(loadWatchedRepo));
			return items.filter((item): item is WatchedRepo => Boolean(item));
		},
	}));

	const watchCount = createMemo(() => watchesQuery.data?.length ?? 0);
	const [watchedPage, setWatchedPage] = createSignal(0);
	const [selectedRepoUri, setSelectedRepoUri] = createSignal<string | null>(null);
	const countRefreshes = new Set<string>();
	const watchedPageCount = createMemo(() => Math.max(1, Math.ceil(watchCount() / WATCHED_PAGE_SIZE)));
	const visibleWatchedRepos = createMemo(() => {
		const items = watchesQuery.data ?? [];
		const start = watchedPage() * WATCHED_PAGE_SIZE;
		return items.slice(start, start + WATCHED_PAGE_SIZE);
	});

	createEffect(() => {
		if (watchedPage() >= watchedPageCount()) {
			setWatchedPage(watchedPageCount() - 1);
		}
	});

	createEffect(() => {
		const items = watchesQuery.data;
		const did = auth.currentDid();
		if (!did || !items) {
			return;
		}

		for (const item of items) {
			const repoDid = item.repo.repoDid;
			if (countRefreshes.has(repoDid)) {
				continue;
			}
			countRefreshes.add(repoDid);
			void Promise.all([
				getAppviewOpenIssueCount(item.repo),
				getAppviewOpenPullCount(item.repo),
			]).then(([openIssueCount, openPullCount]) => {
				queryClient.setQueryData<WatchedRepo[]>([WATCHED_REPOS_QUERY_KEY, did], (current) =>
					current?.map((entry) => entry.repo.repoDid === repoDid
						? { ...entry, openIssueCount, openPullCount }
						: entry),
				);
			}).catch(() => {
				countRefreshes.delete(repoDid);
			});
		}
	});
	const processedLiveEvents = new Set<string>();

	createEffect(() => {
		const items = watchesQuery.data;
		if (!auth.currentDid() || !items) {
			live.clear();
			return;
		}

		const subjects = new Set<string>();
		for (const item of items) {
			subjects.add(item.repo.repoDid);
			item.issues.forEach((issue) => subjects.add(issue.uri));
			item.pulls.forEach((pull) => subjects.add(pull.uri));
		}

		live.subscribe({
			subjects: [...subjects].sort(),
			sources: [
				'sh.tangled.repo.issue:repoDid',
				'sh.tangled.repo.issue:repo',
				'sh.tangled.repo.issue.state:issue',
				'sh.tangled.repo.pull:target.repo',
				'sh.tangled.repo.pull.status:pull',
			],
		});
	});

	createEffect(() => {
		const items = watchesQuery.data;
		const events = live.events();
		if (!auth.currentDid() || !items || events.length === 0) {
			return;
		}

		const watchedSubjects = new Set<string>();
		for (const item of items) {
			watchedSubjects.add(item.repo.repoDid);
			item.issues.forEach((issue) => watchedSubjects.add(issue.uri));
			item.pulls.forEach((pull) => watchedSubjects.add(pull.uri));
		}

		const watchedSources = new Set([
			'sh.tangled.repo.issue:repoDid',
			'sh.tangled.repo.issue:repo',
			'sh.tangled.repo.issue.state:issue',
			'sh.tangled.repo.pull:target.repo',
			'sh.tangled.repo.pull.status:pull',
		]);
		let shouldRefresh = false;
		for (const event of events) {
			const eventKey = `${event.receivedAt}:${event.sourceRecord}`;
			if (processedLiveEvents.has(eventKey)) {
				continue;
			}
			processedLiveEvents.add(eventKey);
			if (watchedSources.has(event.source) && watchedSubjects.has(event.subject)) {
				shouldRefresh = true;
			}
		}

		if (shouldRefresh) {
			countRefreshes.clear();
			void queryClient.invalidateQueries({ queryKey: [WATCHED_REPOS_QUERY_KEY, auth.currentDid()] });
		}
	});

	onCleanup(() => live.clear());

	return (
		<div class="untangled-watched-page">
			<header class="untangled-watched-page-header">
				<h1><Eye class="size-6" /> watched repos</h1>
			</header>

			<Show when={auth.currentDid()} fallback={<div class="untangled-watch-empty"><h2>sign in to watch repositories</h2><p>watch a repository to keep its recent issues, pull requests, and commits in one place.</p></div>}>
				<Show when={!watchesQuery.isLoading} fallback={<WatchedReposSkeleton />}>
					<Show when={!watchesQuery.error} fallback={<ErrorState message={getErrorMessage(watchesQuery.error)} />}>
						<Show when={watchCount() > 0} fallback={<div class="untangled-watch-empty"><h2>nothing watched yet</h2><p>open a repository and choose <strong>watch</strong> to start your daily brief.</p><A href="/search" class="btn mt-2">find a repository</A></div>}>
							<div class="untangled-watched-list" role="list" aria-label="watched repositories">
								<div class="untangled-watched-list-heading" aria-hidden="true">
									<span>repository</span>
									<span>open work</span>
									<span>latest</span>
									<span></span>
								</div>
								<For each={visibleWatchedRepos()}>{(item) => (
									<WatchedRepoRow
										item={item}
										expanded={selectedRepoUri() === item.watch.uri}
										onToggle={() => setSelectedRepoUri((selected) => selected === item.watch.uri ? null : item.watch.uri)}
									/>
								)}</For>
							</div>
							<Show when={watchedPageCount() > 1}>
								<nav class="untangled-watched-pagination" aria-label="watched repository pages">
									<button
										type="button"
										class="untangled-watched-page-button"
										disabled={watchedPage() === 0}
										onClick={() => setWatchedPage((page) => Math.max(0, page - 1))}
										aria-label="previous watched repositories"
									>
										<ChevronLeft class="size-4" />
									</button>
									<span aria-live="polite">{watchedPage() + 1} / {watchedPageCount()}</span>
									<button
										type="button"
										class="untangled-watched-page-button"
										disabled={watchedPage() >= watchedPageCount() - 1}
										onClick={() => setWatchedPage((page) => Math.min(watchedPageCount() - 1, page + 1))}
										aria-label="next watched repositories"
									>
										<ChevronRight class="size-4" />
									</button>
								</nav>
							</Show>
						</Show>
					</Show>
				</Show>
			</Show>
		</div>
	);
};

export const WatchingPage: Component = () => <WatchedReposPanel />;
